import { useEffect, useState } from 'react';
import { heroSlides, stats, whatsappUrl } from '@/mocks/homeData';

export default function HeroSlider() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const go = (dir: number) => {
    setActive((prev) => (prev + dir + heroSlides.length) % heroSlides.length);
  };

  const slide = heroSlides[active];

  return (
    <section id="home" className="relative w-full min-h-[620px] md:min-h-[680px] overflow-hidden bg-primary-950">
      {heroSlides.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === active ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={item.image}
            alt={`${item.title} - Best Research Pharma Ayurvedic products`}
            title={`${item.title} - Best Research Pharma`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/65 to-primary-950/25"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-20 w-full min-h-[620px] md:min-h-[680px] px-4 md:px-8 py-20 md:py-24 flex items-center">
        <div className="w-full max-w-6xl mx-auto">
          <div key={`content-${slide.id}`} className="max-w-2xl">
            <span className="slide-fade inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-background-50/10 backdrop-blur-sm border border-background-50/25 text-background-50 text-xs font-semibold uppercase tracking-[0.16em]">
              <i className="ri-leaf-fill text-accent-300"></i>
              {slide.eyebrow}
            </span>

            <h1 className="slide-fade font-heading mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] text-background-50">
              {slide.title}
            </h1>

            <p className="slide-fade mt-5 text-sm md:text-lg leading-relaxed text-background-100/90 max-w-xl">
              {slide.tagline}
            </p>

            <div className="slide-fade mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <a
                href="#categories"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-bag-3-line text-base"></i>
                Explore Products
              </a>
              <a
                href="#distributor"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-hand-heart-line text-base"></i>
                Become a Distributor
              </a>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-background-50/40 bg-background-50/10 backdrop-blur-sm text-background-50 text-sm font-semibold hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-base"></i>
                Enquire Now
              </a>
            </div>

            <a
              href="#distributor"
              className="animate-fade-up mt-7 hidden md:inline-flex flex-col items-center gap-1.5 group cursor-pointer"
              aria-label="Scroll down to become a distributor"
            >
              <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-background-50/70 group-hover:text-background-50 transition-colors">
                Become a Distributor
              </span>
              <span className="scroll-hint w-8 h-8 flex items-center justify-center rounded-full border border-background-50/40 text-background-50/80 group-hover:border-background-50 group-hover:text-background-50 transition-colors">
                <i className="ri-arrow-down-line text-lg"></i>
              </span>
            </a>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-background-50/20 pt-6 max-w-3xl">
            {stats.map((item) => (
              <div key={item.id}>
                <p className="font-heading text-2xl md:text-3xl font-semibold text-accent-300">{item.value}</p>
                <p className="text-[11px] md:text-xs text-background-100/80 mt-0.5">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="Previous slide"
        onClick={() => go(-1)}
        className="hidden sm:flex absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-background-50/15 hover:bg-background-50/30 backdrop-blur-sm text-background-50 items-center justify-center transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="Next slide"
        onClick={() => go(1)}
        className="hidden sm:flex absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-background-50/15 hover:bg-background-50/30 backdrop-blur-sm text-background-50 items-center justify-center transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 md:bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2.5">
        {heroSlides.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            onClick={() => setActive(index)}
            className={`h-2.5 rounded-full transition-all cursor-pointer ${
              index === active ? 'w-8 bg-accent-400' : 'w-2.5 bg-background-50/50 hover:bg-background-50/80'
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}